import classNames from 'classnames';
import { type CSSProperties, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { useImageContentControlsContext } from '~/components/AssetModalV2/Content/ImageContentControlsContextProvider';
import { useFullscreenContext } from '~/components/AssetModalV2/shared/context/FullscreenContext';
import { ImageMinimap } from '~/components/AssetModalV2/Visualizer/ImageVisualizer/components/ImageMinimap';
import {
  type ForwardedImageVisualizerClipProps,
  ImageVisualizerClip,
} from '~/components/AssetModalV2/Visualizer/ImageVisualizer/components/ImageVisualizerClip';
import {
  type ImageZoomViewState,
  useImageZoom,
} from '~/components/AssetModalV2/Visualizer/ImageVisualizer/hooks/useImageZoom';
import { useAssetHotkeys } from '~/components/AssetModalV2/Visualizer/shared/useAssetHotkeys';
import { EditModeTransformationHintBanner } from '~/components/EditMode/components/EditModeTransformationHintBanner';
import { useEditMode } from '~/components/EditMode/EditModeProvider';

// Inspired by: https://github.com/sylvaindubus/react-prismazoom

export type ImageVisualizerProps = {
  canAnnotateClip: boolean;
  overrideImageContainerStyle?: CSSProperties;
  overrideImageSrc?: string;
  onImageLoad?: () => void;
  children?: ForwardedImageVisualizerClipProps['children'];
} & Pick<ForwardedImageVisualizerClipProps, 'AnnotationsAvatars' | 'AnnotationComment' | 'FloatingCommentForm'>;

export const ImageVisualizer = memo(
  ({
    canAnnotateClip,
    AnnotationsAvatars,
    AnnotationComment,
    FloatingCommentForm,
    overrideImageContainerStyle,
    overrideImageSrc,
    onImageLoad,
    children,
  }: ImageVisualizerProps) => {
    const { isEditModeEnabled } = useEditMode();
    const {
      isMinimapAvailable,
      isMinimapOpen,
      minimapId,
      onMinimapFocusHandled,
      onSetMinimapAvailable,
      onZoomIn,
      onZoomOut,
      showOriginal,
      showSolidBackground,
      shouldFocusMinimap,
      updateZoom,
      zoom,
    } = useImageContentControlsContext();
    const imageRef = useRef<HTMLImageElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const imgContainerRef = useRef<HTMLDivElement | null>(null);
    const sourceIdentity = `${showOriginal ? 'original' : 'preview'}:${overrideImageSrc ?? ''}`;
    const loadedSourceIdentityRef = useRef('');
    const [minimapSrc, setMinimapSrc] = useState('');
    const [viewState, setViewState] = useState<ImageZoomViewState>({ posX: 0, posY: 0, zoom });

    const { isFullscreen, toggleFullscreen } = useFullscreenContext();

    useAssetHotkeys({
      zoomIn: onZoomIn,
      zoomOut: onZoomOut,
      openFullscreen: toggleFullscreen,
      isFullscreen,
    });

    const handleViewChange = useCallback((nextViewState: ImageZoomViewState) => {
      setViewState(nextViewState);
    }, []);

    const { handleMouseMove, handleMouseStart, handleMouseStop, panToImageFraction, reset, zoomToPoint } = useImageZoom(
      {
        zoom,
        imageRef,
        imgContainerRef,
        onViewChange: handleViewChange,
        updateZoom,
      },
    );

    const handleImageReset = useCallback(() => {
      setMinimapSrc('');
    }, []);

    const handleImageError = useCallback(() => {
      setMinimapSrc('');
    }, []);

    const handleImageLoad = useCallback(() => {
      const loadedImageSrc = imageRef.current?.currentSrc || imageRef.current?.src || overrideImageSrc || '';
      loadedSourceIdentityRef.current = sourceIdentity;
      setMinimapSrc(loadedImageSrc);
      onImageLoad?.();
    }, [onImageLoad, overrideImageSrc, sourceIdentity]);

    useIsomorphicLayoutEffect(() => {
      if (loadedSourceIdentityRef.current !== sourceIdentity) {
        setMinimapSrc('');
      }
    }, [sourceIdentity]);

    useEffect(() => {
      onSetMinimapAvailable(!isEditModeEnabled && minimapSrc.length > 0);
    }, [isEditModeEnabled, minimapSrc, onSetMinimapAvailable]);

    useEffect(() => {
      return () => {
        onSetMinimapAvailable(false);
      };
    }, [onSetMinimapAvailable]);

    const shouldShowMinimap = isMinimapAvailable && isMinimapOpen && !isEditModeEnabled && minimapSrc.length > 0;

    return (
      <div
        className={classNames(
          'relative flex overflow-visible transition-colors',
          isFullscreen ? 'fixed top-0 left-0 z-[3] h-screen w-screen' : 'h-full w-full',
          isEditModeEnabled ? 'ads:bg-base bg-gray-1 duration-500' : 'ads:bg-base bg-gray-1 duration-150',
        )}
        ref={containerRef}
        data-testid="IMAGE_VISUALIZER_CONTAINER"
      >
        {isEditModeEnabled && <EditModeTransformationHintBanner />}
        <ImageVisualizerClip
          AnnotationsAvatars={AnnotationsAvatars}
          AnnotationComment={AnnotationComment}
          FloatingCommentForm={FloatingCommentForm}
          canAnnotateClip={canAnnotateClip}
          imageRef={imageRef}
          ref={imgContainerRef}
          zoom={zoom}
          handleMouseStart={handleMouseStart}
          handleMouseMove={handleMouseMove}
          handleMouseStop={handleMouseStop}
          isFullScreen={isFullscreen}
          isShowOriginal={showOriginal}
          isShowSolidBackground={showSolidBackground}
          zoomToPoint={zoomToPoint}
          resetZoom={reset}
          overrideImageContainerStyle={overrideImageContainerStyle}
          overrideImageSrc={overrideImageSrc}
          onImageError={handleImageError}
          onImageLoad={handleImageLoad}
          onImageReset={handleImageReset}
        >
          {children}
        </ImageVisualizerClip>
        {shouldShowMinimap ? (
          <ImageMinimap
            imageRef={imageRef}
            imageSrc={minimapSrc}
            imgContainerRef={imgContainerRef}
            minimapId={minimapId}
            onMinimapFocusHandled={onMinimapFocusHandled}
            onPanToImageFraction={panToImageFraction}
            posX={viewState.posX}
            posY={viewState.posY}
            shouldFocus={shouldFocusMinimap}
            zoom={viewState.zoom}
          />
        ) : null}
      </div>
    );
  },
);

ImageVisualizer.displayName = 'ImageVisualizer';
